import React, { useState } from "react";

import {
  List,
  DataTable,
  TextField,
  SimpleForm,
  TextInput,
  ReferenceInput,
  Edit,
  Create,
  Show,
  EditButton,
  Pagination,
  NumberInput,
  ReferenceField,
  NumberField,
  number,
  minValue,
  ReferenceManyField,
  useRecordContext,
  required,
  useListContext,
  Button,
  TopToolbar,
  FilterButton,
  CreateButton,
  ColumnsButton,
  AutocompleteInput,
  SelectInput,
  BooleanField,
  TabbedShowLayout,
  Tab,
  useNotify,
} from "react-admin";

// Lib
import { custInfo } from "../lib/customer";
import { hasScope, SCOPE } from "../lib/auth";
import { DetailsButton } from "../lib/detailsButton";
import { WineColourField, WineColorInput } from "../lib/wineColour";
import { VolumeField } from "../lib/volume";
import { DownloadCSV } from "../lib/csvDownload";
import { CustomDateTimeInput, CustomDateField } from "../lib/datetime";

//MUI
import IconDownload from "@mui/icons-material//CloudDownload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconCancel from "@mui/icons-material//Cancel";
import DialogActions from "@mui/material/DialogActions";

import { LotNameField } from "./lots";
import { CustomTab } from "../lib/customTab";

const validateYear = [number(), minValue(2000)];

const BlendAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span> {"Blend " + record.name}</span>;
};

const archivedFilterChoices = [
  { id: false, name: "Hide archived" },
  { id: true, name: "Show only archived" },
];

const blendFilters = [
  <TextInput label="Blend" source="name" alwaysOn resettable />,
  <SelectInput
    source="archived"
    label="Archived"
    alwaysOn
    choices={archivedFilterChoices}
    emptyText="Show all"
  />,
];

const BlendExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();

  const handleDownload = async () => {
    setShowDialog(false);

    try {
      await DownloadCSV(
        "BarrelWise_Blends",
        custInfo.customerID,
        "blends",
        sort,
        filterValues,
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const BlendListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <CreateButton disabled={!hasScope(SCOPE.MANAGER)} />
      <BlendExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

export const BlendList = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    actions={<BlendListActions />}
    filters={blendFilters}
    filterDefaultValues={{ archived: false }}
    sort={{ field: "name", order: "ASC" }}
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="name" label="Blend">
        <ReferenceField source="id" reference="blends" link="show">
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="date" label="Creation Date">
        <CustomDateField source="date" />
      </DataTable.Col>
      <DataTable.Col source="year" label="Vintage">
        <TextField source="year" label="Vintage" />
      </DataTable.Col>
      <DataTable.Col source="varietalID" label="Varietal">
        <ReferenceField source="varietalID" reference="varietals" link={false}>
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="isRed" label="Color">
        <WineColourField source="isRed" label="Color" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
      {hasScope(SCOPE.MANAGER) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
    </DataTable>
  </List>
);

const CreateEditForm = () => (
  <SimpleForm>
    <TextInput label="Blend" source="name" validate={required()} />
    <CustomDateTimeInput
      label="Creation Date"
      source="date"
      defaultValue={new Date()}
    />
    <NumberInput label="Vintage" source="year" validate={validateYear} />
    <ReferenceInput
      source="varietalID"
      reference="varietals"
      defaultValue={0}
      sort={{ field: "name", order: "ASC" }}
    >
      <AutocompleteInput
        optionText="name"
        filterToQuery={(searchText) => ({ name: searchText })}
        label="Varietal"
      />
    </ReferenceInput>
    {hasScope(SCOPE.ADMIN) ? (
      <WineColorInput label="Color" source="isRed" />
    ) : null}
  </SimpleForm>
);

export const BlendEdit = () => (
  <Edit title={<BlendAppBarTitle />} redirect="list">
    <CreateEditForm />
  </Edit>
);

export const BlendCreate = () => (
  <Create redirect="list">
    <CreateEditForm />
  </Create>
);

export const BlendShow = () => (
  <Show title={<BlendAppBarTitle />}>
    <TabbedShowLayout>
      <Tab label="Overview">
        <TextField source="name" label="Blend Name" />
        <CustomDateField source="date" label="Creation Date" sortable={false} />
        <TextField source="year" label="Year" emptyText={"\u00A0"} />
        <ReferenceField
          label="Varietal"
          source="varietalID"
          reference="varietals"
          sortable={false}
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <WineColourField source="isRed" label="Color" />
        <BooleanField source="archived" label="Archived" />
      </Tab>
      <Tab label="Lots">
        <ReferenceManyField
          label=""
          reference="lots"
          target="blendID"
          perPage={1000}
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="id" label="Lot" disableSort>
              <ReferenceField
                source="id"
                reference="lots"
                link="show"
                sortable={false}
              >
                <LotNameField />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="blendID" label="Blend" disableSort>
              <ReferenceField
                source="blendID"
                reference="blends"
                link={false}
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col
              source="intendedUse"
              label="Intended Use"
              disableSort
            >
              <TextField source="intendedUse" label="Intended Use" />
            </DataTable.Col>
            <DataTable.Col source="addTarget" label="Target" disableSort>
              <NumberField source="addTarget" label="Target" />
            </DataTable.Col>
            <DataTable.Col source="barrelCount" label="Vessels" disableSort>
              <NumberField source="barrelCount" label="Vessels" />
            </DataTable.Col>
            <DataTable.Col source="capacity" label="Capacity">
              <VolumeField source="capacity" label="Capacity" />
            </DataTable.Col>
          </DataTable>
        </ReferenceManyField>
      </Tab>
      <CustomTab />
    </TabbedShowLayout>
  </Show>
);
